import React, { useMemo } from 'react';

type PropertyTypeMarketRow = {
  propertyType?: string;
  median?: number | null;
  average?: number | null;
  medianPerSqFt?: number | null;
  averagePerSqFt?: number | null;
  medianDaysOnMarket?: number | null;
  averageDaysOnMarket?: number | null;
  totalListings?: number | null;
};

const money = (value: number) => `$${Math.round(value).toLocaleString()}`;
const compactMoney = (value: number) =>
  Math.abs(value) >= 1_000_000
    ? `$${(value / 1_000_000).toFixed(1)}M`
    : Math.abs(value) >= 1000
      ? `$${Math.round(value / 1000)}K`
      : money(value);

const displayPropertyType = (value?: string) => {
  if (!value) return 'Other';
  return value
    .replace(/([a-z])([A-Z])/g, '$1 $2')
    .replace(/[_-]+/g, ' ')
    .replace(/\b\w/g, (letter) => letter.toUpperCase());
};

const SalePropertyTypeAnalysisCard: React.FC<{ rows?: PropertyTypeMarketRow[] }> = ({ rows }) => {
  const data = useMemo(
    () =>
      (rows || [])
        .filter((row) => row.propertyType)
        .map((row) => ({
          propertyType: displayPropertyType(row.propertyType),
          median: Number(row.median) || 0,
          average: Number(row.average) || 0,
          perSqFt: Number(row.medianPerSqFt ?? row.averagePerSqFt) || 0,
          dom: Number(row.medianDaysOnMarket ?? row.averageDaysOnMarket) || 0,
          listings: Number(row.totalListings) || 0,
        }))
        .sort((a, b) => a.propertyType.localeCompare(b.propertyType)),
    [rows],
  );

  if (!data.length) return null;

  const scaleMax = Math.max(...data.flatMap((row) => [row.median, row.average]), 1);
  const deepestMarket = [...data].sort((a, b) => b.listings - a.listings)[0];

  return (
    <div
      style={{
        border: '1px solid #e8eef3',
        borderRadius: 14,
        background: '#fff',
        boxShadow: '0 2px 10px rgba(15, 23, 42, 0.06)',
        padding: 16,
        marginBottom: 16,
      }}
    >
      <div
        style={{
          display: 'flex',
          alignItems: 'center',
          gap: 8,
          color: '#12858d',
          fontSize: 18,
          fontWeight: 800,
          marginBottom: 14,
        }}
      >
        <span
          style={{
            width: 28,
            height: 28,
            borderRadius: '50%',
            background: '#dff4f5',
            display: 'inline-flex',
            alignItems: 'center',
            justifyContent: 'center',
            fontSize: 16,
          }}
        >
          ↗
        </span>
        Sale Data by Property Type
      </div>

      <div
        style={{
          display: 'grid',
          gridTemplateColumns: 'minmax(0, 1fr) minmax(170px, 200px)',
          gap: 16,
          alignItems: 'stretch',
        }}
      >
        <div
          style={{
            minWidth: 0,
            overflowX: 'auto',
            paddingBottom: 8,
            WebkitOverflowScrolling: 'touch',
          }}
        >
          <div
            style={{
              display: 'flex',
              justifyContent: 'space-between',
              alignItems: 'center',
              marginBottom: 12,
              color: '#334155',
              fontSize: 11,
              minWidth: 680,
            }}
          >
            <div style={{ display: 'flex', gap: 24 }}>
              <span><span style={{ color: '#3288e9', fontSize: 16 }}>▬</span> Median Sale Price</span>
              <span><span style={{ color: '#12a3aa', fontSize: 16 }}>●</span> Average Sale Price</span>
            </div>
            <span>All prices in USD</span>
          </div>

          <div
            style={{
              display: 'grid',
              gridTemplateColumns: '126px minmax(240px, 1fr) 82px 62px 70px',
              gap: 10,
              paddingBottom: 8,
              fontSize: 11,
              fontWeight: 700,
              color: '#17233d',
              minWidth: 680,
            }}
          >
            <div>Property Type</div>
            <div>Sale Price (USD)</div>
            <div>$/sq ft</div>
            <div>DOM</div>
            <div>Listings</div>
          </div>

          {data.map((row) => {
            const medianWidth = Math.max((row.median / scaleMax) * 100, row.median > 0 ? 3 : 0);
            const averagePosition = Math.min((row.average / scaleMax) * 100, 96);
            return (
              <div
                key={row.propertyType}
                style={{
                  display: 'grid',
                  gridTemplateColumns: '126px minmax(240px, 1fr) 82px 62px 70px',
                  gap: 10,
                  alignItems: 'center',
                  minHeight: 88,
                  borderTop: '1px solid #edf0f2',
                  minWidth: 680,
                }}
              >
                <div>
                  <div style={{ fontSize: 13, fontWeight: 700, color: '#17233d' }}>{row.propertyType}</div>
                  {row.listings <= 2 && (
                    <div
                      style={{
                        display: 'inline-block',
                        marginTop: 5,
                        border: '1px solid #efc99b',
                        background: '#fff8ef',
                        color: '#9a5d20',
                        borderRadius: 6,
                        padding: '4px 5px',
                        fontSize: 9,
                        lineHeight: 1.3,
                      }}
                    >
                      Low confidence ⚠<br />({row.listings} {row.listings === 1 ? 'listing' : 'listings'})
                    </div>
                  )}
                </div>

                <div style={{ position: 'relative', height: 50 }}>
                  <div
                    style={{
                      position: 'absolute',
                      left: 0,
                      top: 15,
                      width: `${medianWidth}%`,
                      height: 14,
                      background: 'linear-gradient(90deg, #4b9bf0, #3288e9)',
                      borderRadius: 2,
                    }}
                  />
                  <span
                    style={{
                      position: 'absolute',
                      left: `min(calc(${medianWidth}% + 7px), calc(100% - 62px))`,
                      top: 12,
                      fontSize: 11,
                      color: '#2778dd',
                      fontWeight: 700,
                      whiteSpace: 'nowrap',
                    }}
                  >
                    {money(row.median)}
                  </span>
                  <span
                    style={{
                      position: 'absolute',
                      left: `min(${averagePosition}%, calc(100% - 72px))`,
                      top: -5,
                      color: '#1098a0',
                      fontSize: 11,
                      fontWeight: 700,
                      whiteSpace: 'nowrap',
                    }}
                  >
                    ● {money(row.average)}
                  </span>
                  <div
                    style={{
                      position: 'absolute',
                      left: 0,
                      right: 0,
                      bottom: 0,
                      height: 1,
                      background: '#eef2f6',
                    }}
                  />
                  <div
                    style={{
                      position: 'absolute',
                      left: 0,
                      bottom: -14,
                      right: 0,
                      display: 'flex',
                      justifyContent: 'space-between',
                      fontSize: 8,
                      color: '#94a3b8',
                    }}
                  >
                    <span>$0</span><span>{compactMoney(scaleMax / 2)}</span><span>{compactMoney(scaleMax)}</span>
                  </div>
                </div>

                <div style={{ textAlign: 'center', fontSize: 11, color: '#334155' }}>
                  {row.perSqFt ? (
                    <><strong>${row.perSqFt.toFixed(2)}</strong><br /><span style={{ color: '#64748b' }}>/sq ft</span></>
                  ) : (
                    <span style={{ color: '#64748b' }}>No $/sq ft<br />reported</span>
                  )}
                </div>
                <div
                  style={{
                    justifySelf: 'center',
                    minWidth: 46,
                    background: '#fff3dc',
                    borderRadius: 7,
                    textAlign: 'center',
                    padding: '7px 4px',
                    color: '#6b4b18',
                    fontSize: 10,
                  }}
                >
                  <strong style={{ fontSize: 13 }}>{Math.round(row.dom)}</strong><br />DOM
                </div>
                <div
                  style={{
                    justifySelf: 'center',
                    minWidth: 48,
                    background: '#f1effd',
                    borderRadius: 7,
                    textAlign: 'center',
                    padding: '7px 4px',
                    color: '#58478a',
                    fontSize: 10,
                  }}
                >
                  <strong style={{ fontSize: 13 }}>{row.listings}</strong><br />
                  {row.listings === 1 ? 'Listing' : 'Listings'}
                </div>
              </div>
            );
          })}
        </div>

        <aside
          style={{
            borderRadius: 12,
            border: '1px solid #dce8f5',
            background: 'linear-gradient(180deg, #eff7ff 0%, #f8fbff 100%)',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            textAlign: 'center',
            padding: 18,
          }}
        >
          <div>
            <div
              style={{
                width: 58,
                height: 58,
                borderRadius: '50%',
                background: '#fff',
                color: '#2778dd',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                margin: '0 auto 12px',
                fontSize: 30,
              }}
            >
              ♧
            </div>
            <div style={{ fontSize: 17, fontWeight: 800, color: '#17233d' }}>Key Insight</div>
            <div style={{ width: 36, height: 3, borderRadius: 2, background: '#2778dd', margin: '10px auto 18px' }} />
            <div style={{ fontSize: 15, lineHeight: 1.55, fontWeight: 700, color: '#17233d' }}>
              {deepestMarket.propertyType} sales dominate the market depth with {deepestMarket.listings} listings.
            </div>
          </div>
        </aside>
      </div>
    </div>
  );
};

export default SalePropertyTypeAnalysisCard;
